/**
 * Filter controls for the submissions list
 */

import React from 'react';
import { Filter, Mail, Calendar, Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { DateRangeDays } from './types';

// Date range options
const DATE_RANGE_OPTIONS: { label: string; value: DateRangeDays }[] = [
  { label: 'All time', value: null },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

interface SubmissionsFiltersProps {
  unreadOnly: boolean;
  dateRangeDays: DateRangeDays;
  search: string;
  onUnreadOnlyChange: (value: boolean) => void;
  onDateRangeChange: (value: DateRangeDays) => void;
  onSearchChange: (value: string) => void;
}

export const SubmissionsFilters: React.FC<SubmissionsFiltersProps> = ({
  unreadOnly,
  dateRangeDays,
  search,
  onUnreadOnlyChange,
  onDateRangeChange,
  onSearchChange,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-4 mb-6">
      {/* Search input */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search submissions..."
          className="w-full pl-9 pr-8 py-1.5 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center text-sm text-gray-600">
        <Filter className="w-4 h-4 mr-2" />
        Filters:
      </div>

      {/* Date range dropdown */}
      <div className="flex items-center">
        <Calendar className="w-4 h-4 mr-1.5 text-gray-500" />
        <select
          value={dateRangeDays === null ? '' : String(dateRangeDays)}
          onChange={e => {
            const val = e.target.value;
            onDateRangeChange(val === '' ? null : (Number(val) as DateRangeDays));
          }}
          className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-1.5 pl-2 pr-8"
        >
          {DATE_RANGE_OPTIONS.map(option => (
            <option key={option.value ?? 'all'} value={option.value === null ? '' : option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={() => onUnreadOnlyChange(!unreadOnly)}
        className={cn(
          'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
          unreadOnly
            ? 'bg-primary-100 text-primary-700 border border-primary-300'
            : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200'
        )}
      >
        <Mail className="w-4 h-4 mr-1.5" />
        Unread only
      </button>
    </div>
  );
};
